/*
 * Diva Catwalk measurement popup and collection size finder.
 * No complete approved store size chart was present in the theme. The table keeps
 * the earlier provisional values and adds the XXXL measurements supplied for the
 * reported issue. Replace it with the merchant-approved chart when available.
 */
(() => {
  if (window.__divaMeasurementPopupInitialized) return;
  window.__divaMeasurementPopupInitialized = true;

  const SIZE_CHART = {
    XS: { bust: 84, waist: 66, hips: 90 },
    S: { bust: 88, waist: 70, hips: 94 },
    M: { bust: 92, waist: 74, hips: 98 },
    L: { bust: 97, waist: 79, hips: 103 },
    XL: { bust: 102, waist: 84, hips: 108 },
    XXL: { bust: 107, waist: 89, hips: 113 },
    XXXL: { bust: 122, waist: 105, hips: 127 },
  };
  const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  const FIT_WEIGHTS = { bust: 0.4, waist: 0.35, hips: 0.25 };
  const MEASUREMENT_TOLERANCE_CM = { bust: 5, waist: 5, hips: 5 };
  const SECONDARY_SIZE_SCORE_GAP = 0.61;
  const STORAGE_KEY = 'divacatwalk_size_finder';

  function init() {
    const popup = document.getElementById('measurement-popup');
    const preorderCheckbox = document.getElementById('preorder-agree-checkbox');
    const preorderConfirm = document.getElementById('preorder-confirm-btn');
    const submitButton = document.getElementById('submit-measurements');
    const closeButton = popup?.querySelector('[data-close-measurement-popup]');
    const errorMessage = document.getElementById('measurement-error');
    const toggle = document.getElementById('unit-toggle');
    const toggleCircle = document.getElementById('toggle-circle');
    const heightCm = document.getElementById('measure-height-cm');
    const heightInchWrapper = document.getElementById('height-inch-wrapper');
    const heightFt = document.getElementById('measure-height-ft');
    const heightIn = document.getElementById('measure-height-in');
    const productInfo = document.querySelector('product-info');
    let productSizeData = null;
    try {
      const dataNode = productInfo?.querySelector('[data-product-size-finder-data]');
      if (dataNode) productSizeData = JSON.parse(dataNode.textContent);
    } catch (error) {
      console.error('[Diva size finder] Could not read current product variant data.', error);
    }
    const productSizeStatus = document.querySelector('[data-product-size-status]');
    const productSizeAvailableDialog = document.getElementById('product-size-available-popup');
    const productSizeAvailableMessage = productSizeAvailableDialog?.querySelector('[data-product-size-available-message]');
    const productSizeAlternativeDialog = document.getElementById('product-size-alternatives-popup');
    const alternativeCards = productSizeAlternativeDialog?.querySelector('[data-product-alternative-cards]');
    const alternativeLoading = productSizeAlternativeDialog?.querySelector('[data-product-alternative-loading]');
    const alternativeEmpty = productSizeAlternativeDialog?.querySelector('[data-product-alternative-empty]');
    const alternativeMessage = productSizeAlternativeDialog?.querySelector('[data-product-alternative-message]');
    const alternativeCollectionLink = productSizeAlternativeDialog?.querySelector('[data-product-alternative-collection-link]');
    if (!popup) return;

    let currentUnit = 'cm';
    let measurementMode = 'preorder';
    let opener = null;
    let alternativeOpener = null;
    let availableDialogOpener = null;

    function closeAvailableDialog() {
      if (!productSizeAvailableDialog) return;
      productSizeAvailableDialog.classList.remove('active');
      productSizeAvailableDialog.hidden = true;
      if (availableDialogOpener?.focus) availableDialogOpener.focus();
      availableDialogOpener = null;
    }

    function openAvailableDialog(size, variant, trigger) {
      if (!productSizeAvailableDialog) return;
      availableDialogOpener = trigger || document.activeElement;
      if (productSizeAvailableMessage) {
        productSizeAvailableMessage.textContent = `Good news! Size ${size} is available${isPreorderVariant(variant) ? ' for preorder' : ''}. It has been selected for you.`;
      }
      productSizeAvailableDialog.hidden = false;
      productSizeAvailableDialog.classList.add('active');
      productSizeAvailableDialog.querySelector('[data-close-product-size-available]')?.focus();
    }

    productSizeAvailableDialog?.querySelectorAll('[data-close-product-size-available]').forEach((button) => {
      button.addEventListener('click', closeAvailableDialog);
    });
    productSizeAvailableDialog?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeAvailableDialog();
      }
    });

    const openPopup = (mode, trigger) => {
      measurementMode = mode;
      opener = trigger || document.activeElement;
      if (mode === 'productSizeFinder' && productSizeStatus) productSizeStatus.hidden = true;
      if (mode !== 'preorder') fillMeasurementsFromSaved();
      popup.classList.add('active');
      popup.setAttribute('data-measurement-mode', mode);
      popup.focus();
      const firstInput = popup.querySelector('#measure-bust');
      if (firstInput) firstInput.focus();
    };

    const closePopup = () => {
      popup.classList.remove('active');
      popup.removeAttribute('data-measurement-mode');
      if (opener && typeof opener.focus === 'function') opener.focus();
      opener = null;
      measurementMode = 'preorder';
    };

    closeButton?.addEventListener('click', closePopup);

    document.querySelectorAll('[data-open-size-finder]').forEach((button) => {
      button.addEventListener('click', () => openPopup('collectionSizeFinder', button));
    });

    document.querySelectorAll('[data-open-product-size-finder]').forEach((button) => {
      button.addEventListener('click', () => {
        // Reopening the fit finder should let customers review or edit their saved
        // measurements. Availability is checked again only after they submit.
        openPopup('productSizeFinder', button);
      });
    });

    document.querySelectorAll('[data-change-product-size-measurements]').forEach((button) => {
      button.addEventListener('click', () => {
        closeAlternativeDialog();
        openPopup('productSizeFinder', button);
      });
    });

    document.querySelectorAll('[data-close-product-size-alternatives]').forEach((button) => {
      button.addEventListener('click', closeAlternativeDialog);
    });

    if (preorderCheckbox && preorderConfirm) {
      preorderCheckbox.addEventListener('change', function () {
        preorderConfirm.disabled = !this.checked;
      });
      preorderConfirm.addEventListener('click', (event) => {
        event.preventDefault();
        const terms = document.getElementById('preorder-terms-popup');
        if (terms) terms.classList.remove('active');
        openPopup('preorder', preorderConfirm);
      });
    }

    popup.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePopup();
      }
      if (event.key !== 'Tab') return;
      const focusable = [...popup.querySelectorAll('button:not([disabled]), input:not([disabled]), [href], [tabindex="0"]')]
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    productSizeAlternativeDialog?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeAlternativeDialog();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...productSizeAlternativeDialog.querySelectorAll('button:not([disabled]), a[href]')]
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      if (event.shiftKey && document.activeElement === focusable[0]) {
        event.preventDefault();
        focusable[focusable.length - 1].focus();
      } else if (!event.shiftKey && document.activeElement === focusable[focusable.length - 1]) {
        event.preventDefault();
        focusable[0].focus();
      }
    });

    function updatePlaceholders() {
      const suffix = currentUnit === 'cm' ? '(cm)' : '(inch)';
      [['measure-bust', 'bust'], ['measure-waist', 'waist'], ['measure-hips', 'hips'],
        ['measure-sleeve', 'sleeve'], ['measure-trouser', 'length']].forEach(([id, label]) => {
        const input = document.getElementById(id);
        if (input) input.placeholder = `Enter ${label} ${suffix}`;
      });
      if (heightCm && currentUnit === 'cm') heightCm.placeholder = 'Enter height (cm)';
    }

    if (toggle) {
      toggle.setAttribute('aria-label', 'Use inches instead of centimeters');
      toggle.addEventListener('change', function () {
        const nextUnit = this.checked ? 'inch' : 'cm';
        if (nextUnit !== currentUnit) {
          ['bust', 'waist', 'hips', 'trouser', 'sleeve'].forEach((key) => {
            const input = document.getElementById(`measure-${key}`);
            const value = Number(input?.value);
            if (input && input.value.trim() && Number.isFinite(value)) {
              input.value = (nextUnit === 'inch' ? value / 2.54 : value * 2.54).toFixed(1);
            }
          });
        }
        currentUnit = nextUnit;
        if (toggleCircle) toggleCircle.style.transform = this.checked ? 'translateX(20px)' : 'translateX(0px)';
        updatePlaceholders();
        if (heightCm) heightCm.style.display = this.checked ? 'none' : 'block';
        if (heightInchWrapper) heightInchWrapper.style.display = this.checked ? 'flex' : 'none';
      });
    }
    updatePlaceholders();

    const sizeFinder = document.querySelector('[data-size-finder]');
    const status = sizeFinder?.querySelector('[data-size-finder-status]');
    const clearButton = sizeFinder?.querySelector('[data-clear-size-finder]');
    const options = [...(sizeFinder?.querySelectorAll('[data-size-filter-option]') || [])];

    function normalizedSizeLabel(label) {
      const value = String(label || '').trim().toUpperCase().replace(/\s+/g, '');
      return ({ '2XL': 'XXL', 'XXL': 'XXL', '2X': 'XXL', '3XL': 'XXXL', 'XXXL': 'XXXL' })[value] || value;
    }

    function getFilterOptions(sizes) {
      const wanted = sizes.map(normalizedSizeLabel);
      return options.filter((option) => wanted.includes(normalizedSizeLabel(option.dataset.label)));
    }

    function readSavedRecommendation() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
        return saved && SIZE_CHART[saved.primary] ? saved : null;
      } catch (_) {
        return null;
      }
    }

    function fillMeasurementsFromSaved() {
      const saved = readSavedRecommendation();
      if (!saved?.measurements) return;
      currentUnit = 'cm';
      if (toggle) toggle.checked = false;
      if (toggleCircle) toggleCircle.style.transform = 'translateX(0px)';
      if (heightCm) heightCm.style.display = 'block';
      if (heightInchWrapper) heightInchWrapper.style.display = 'none';
      ['bust', 'waist', 'hips'].forEach((key) => {
        const input = document.getElementById(`measure-${key}`);
        if (input && Number.isFinite(Number(saved.measurements[key]))) {
          input.value = String(saved.measurements[key]);
          input.removeAttribute('aria-invalid');
        }
      });
      if (errorMessage) errorMessage.style.display = 'none';
      updatePlaceholders();
    }

    function closeAlternativeDialog() {
      if (!productSizeAlternativeDialog) return;
      productSizeAlternativeDialog.classList.remove('active');
      productSizeAlternativeDialog.hidden = true;
      if (alternativeOpener?.focus) alternativeOpener.focus();
      alternativeOpener = null;
    }

    function openAlternativeDialog(size, trigger) {
      if (!productSizeAlternativeDialog) return;
      alternativeOpener = trigger || document.activeElement;
      const sizeLabel = productSizeAlternativeDialog.querySelector('[data-alternative-recommended-size]');
      if (sizeLabel) sizeLabel.textContent = size;
      if (alternativeMessage) {
        alternativeMessage.textContent = `This product is not available in size ${size}. Here are some alternatives with that size available.`;
      }
      if (alternativeCards) alternativeCards.replaceChildren();
      if (alternativeEmpty) alternativeEmpty.hidden = true;
      if (alternativeLoading) alternativeLoading.hidden = false;
      if (alternativeCollectionLink) {
        const firstCollection = productSizeData?.collections?.[0];
        alternativeCollectionLink.href = firstCollection?.url || '/collections/all';
        alternativeCollectionLink.hidden = false;
      }
      productSizeAlternativeDialog.hidden = false;
      productSizeAlternativeDialog.classList.add('active');
      productSizeAlternativeDialog.querySelector('[data-close-product-size-alternatives]')?.focus();
      return findAlternativeProducts(size);
    }

    function getProductOptionIndex(optionName) {
      return (productSizeData?.optionNames || []).findIndex((name) => String(name).trim().toLowerCase() === optionName);
    }

    function isPreorderVariant(variant) {
      const preorderEnabled = variant.preorder === true || variant.preorder === 'true';
      return preorderEnabled && Number(variant.inventoryQuantity) > 0;
    }

    function getCurrentProductVariant() {
      const selectedVariantId = productInfo?.querySelector('.product-variant-id')?.value;
      return productSizeData?.variants?.find((variant) => String(variant.id) === String(selectedVariantId)) || null;
    }

    function findAvailableProductVariant(size) {
      if (!productSizeData?.variants?.length) return null;
      const sizeIndex = getProductOptionIndex('size');
      if (sizeIndex < 0) return null;
      const colourIndex = (productSizeData.optionNames || []).findIndex((name) => ['colour', 'color'].includes(String(name).trim().toLowerCase()));
      const selectedVariant = getCurrentProductVariant();
      const selectedColour = colourIndex >= 0 ? selectedVariant?.options?.[colourIndex] : null;
      const matches = productSizeData.variants.filter((variant) =>
        normalizedSizeLabel(variant.options?.[sizeIndex]) === normalizedSizeLabel(size)
          && (variant.available === true || isPreorderVariant(variant))
      );
      matches.sort((left, right) => {
        const leftMatchesColour = selectedColour && left.options?.[colourIndex] === selectedColour ? 1 : 0;
        const rightMatchesColour = selectedColour && right.options?.[colourIndex] === selectedColour ? 1 : 0;
        return rightMatchesColour - leftMatchesColour;
      });
      return matches[0] || null;
    }

    function selectProductVariant(variant) {
      return new Promise((resolve) => {
        const variantSelects = productInfo?.querySelector('variant-selects');
        if (!variantSelects || !productSizeData?.optionNames?.length) {
          resolve(String(productInfo?.querySelector('.product-variant-id')?.value) === String(variant.id));
          return;
        }
        const controls = [];
        let sizeControl = null;
        productSizeData.optionNames.forEach((optionName, index) => {
          const value = variant.options?.[index];
          if (value == null) return;
          const select = [...variantSelects.querySelectorAll('select[name^="options["]')]
            .find((control) => control.name === `options[${optionName}]`);
          if (select) {
            const option = [...select.options].find((entry) => entry.value === value);
            if (!option) return;
            select.value = value;
            select.querySelectorAll('option').forEach((entry) => entry.removeAttribute('selected'));
            option.setAttribute('selected', 'selected');
            controls.push(select);
            if (String(optionName).trim().toLowerCase() === 'size') sizeControl = select;
            return;
          }
          const radioName = `${optionName}-${index + 1}`;
          const radio = [...variantSelects.querySelectorAll('input[type="radio"]')]
            .find((input) => input.name === radioName && input.value === value);
          if (radio) {
            radio.checked = true;
            controls.push(radio);
            if (String(optionName).trim().toLowerCase() === 'size') sizeControl = radio;
          }
        });

        if (!sizeControl) {
          resolve(false);
          return;
        }
        const currentId = productInfo.querySelector('.product-variant-id')?.value;
        if (String(currentId) === String(variant.id)) {
          resolve(true);
          return;
        }
        sizeControl.dispatchEvent(new Event('change', { bubbles: true }));
        let checks = 0;
        const poll = () => {
          const selectedId = productInfo.querySelector('.product-variant-id')?.value;
          if (String(selectedId) === String(variant.id)) resolve(true);
          else if (++checks >= 60) resolve(false);
          else window.setTimeout(poll, 100);
        };
        window.setTimeout(poll, 50);
      });
    }

    async function handleProductSizeRecommendation(recommendation, trigger) {
      const returnTarget = trigger || opener;
      const size = recommendation?.primary;
      if (productSizeStatus) {
        productSizeStatus.hidden = false;
        productSizeStatus.textContent = `Checking whether this product is available in size ${size || ''}…`;
      }
      if (!size || !productInfo || !productSizeData) {
        if (popup.classList.contains('active') && errorMessage) {
          errorMessage.textContent = 'We could not read this product’s available sizes. Please close this message and choose a size manually.';
          errorMessage.style.display = 'block';
        } else if (productSizeStatus) {
          productSizeStatus.hidden = false;
          productSizeStatus.textContent = 'We could not read this product’s available sizes. Please choose a size manually.';
        }
        return;
      }
      const variant = findAvailableProductVariant(size);
      if (variant) {
        const colourIndex = (productSizeData.optionNames || []).findIndex((name) => ['colour', 'color'].includes(String(name).trim().toLowerCase()));
        const currentVariant = getCurrentProductVariant();
        const changedColour = colourIndex >= 0
          && currentVariant?.options?.[colourIndex]
          && currentVariant.options[colourIndex] !== variant.options?.[colourIndex];
        closePopup();
        if (productSizeStatus) {
          productSizeStatus.hidden = false;
          productSizeStatus.textContent = `Selecting your recommended size ${size}…`;
        }
        const selected = await selectProductVariant(variant);
        if (selected) {
          openAvailableDialog(size, variant, returnTarget);
          if (productSizeStatus) {
            const colorNote = changedColour ? ` We selected ${variant.options[colourIndex]} because it is available in that size.` : '';
            productSizeStatus.textContent = `Your recommended size is ${size}. This product is available in your recommended size${isPreorderVariant(variant) ? ' for preorder' : ''}.${colorNote}`;
          }
          document.querySelectorAll('[data-product-size-finder-controls] [data-change-product-size-measurements]').forEach((button) => { button.hidden = false; });
        } else {
          if (productSizeStatus) productSizeStatus.textContent = 'We could not update this product’s size selector. Here are products available in your recommended size.';
          await openAlternativeDialog(size, returnTarget);
        }
        return;
      }
      closePopup();
      if (productSizeStatus) productSizeStatus.hidden = true;
      await openAlternativeDialog(size, returnTarget);
    }

    function createAlternativeCard(productItem, size) {
      const link = [...productItem.querySelectorAll('a[href*="/products/"]')]
        .find((candidate) => !candidate.classList.contains('product-image-overlay-link'))
        || productItem.querySelector('a[href*="/products/"]');
      if (!link) return null;
      const image = productItem.querySelector('.card__media img, img');
      const titleNode = productItem.querySelector('.card__heading, .full-unstyled-link');
      const priceNode = productItem.querySelector('.price-item--sale') || productItem.querySelector('.price-item--regular');
      const card = document.createElement('li');
      card.className = 'product-size-alternatives-popup__card';
      const anchor = document.createElement('a');
      anchor.href = link.href;
      const imageSrc = image?.currentSrc || image?.getAttribute('src') || image?.dataset.src || image?.src;
      if (imageSrc) {
        const cardImage = document.createElement('img');
        cardImage.src = imageSrc;
        cardImage.alt = image.alt || titleNode?.textContent.trim() || '';
        cardImage.loading = 'lazy';
        anchor.append(cardImage);
      }
      const title = document.createElement('p');
      title.className = 'product-size-alternatives-popup__card-title';
      title.textContent = titleNode?.textContent.trim() || link.textContent.trim() || 'View product';
      anchor.append(title);
      card.append(anchor);
      if (priceNode) {
        const price = document.createElement('p');
        price.className = 'product-size-alternatives-popup__card-price';
        price.textContent = priceNode.textContent.trim();
        card.append(price);
      }
      const availability = document.createElement('p');
      availability.className = 'product-size-alternatives-popup__card-availability';
      availability.textContent = `Available in ${size}`;
      card.append(availability);
      const viewProduct = document.createElement('a');
      viewProduct.className = 'product-size-alternatives-popup__view button button--secondary';
      viewProduct.href = link.href;
      viewProduct.textContent = 'View Product';
      card.append(viewProduct);
      return card;
    }

    function productHandleFromUrl(url) {
      return new URL(url, window.location.origin).pathname.match(/\/products\/([^/]+)/)?.[1] || null;
    }

    function productHasPurchasableSize(productItem, size) {
      try {
        const variants = JSON.parse(productItem.dataset.sizeFinderVariants || '[]');
        return variants.some((variant) =>
          normalizedSizeLabel(variant.size) === normalizedSizeLabel(size)
          && (variant.available === true || isPreorderVariant(variant))
        );
      } catch (_) {
        return false;
      }
    }

    async function findAlternativeProducts(size) {
      if (!productSizeData?.collections?.length || !alternativeCards) {
        if (alternativeLoading) alternativeLoading.hidden = true;
        if (alternativeEmpty) {
          alternativeEmpty.hidden = false;
          alternativeEmpty.textContent = 'We could not find a collection with a Size filter for this product.';
        }
        return;
      }
      let requestFailed = false;
      const seenProducts = new Set();
      const currentHandle = productHandleFromUrl(productSizeData.productUrl);
      const collections = productSizeData.collections.slice(0, 8);
      for (const productCollection of collections) {
        if (alternativeCards.children.length >= 6) break;
        try {
          const collectionUrl = new URL(productCollection.url, window.location.origin);
          const collectionResponse = await fetch(collectionUrl.href, { credentials: 'same-origin' });
          if (!collectionResponse.ok) throw new Error(`Collection request failed (${collectionResponse.status})`);
          const collectionHtml = new DOMParser().parseFromString(await collectionResponse.text(), 'text/html');
          const sizeOption = [...collectionHtml.querySelectorAll('[data-size-filter-option]')]
            .find((option) => normalizedSizeLabel(option.dataset.label) === normalizedSizeLabel(size));
          if (!sizeOption) continue;

          const filteredUrl = new URL(collectionUrl.href);
          filteredUrl.searchParams.set(sizeOption.dataset.param, sizeOption.dataset.value);
          filteredUrl.searchParams.delete('page');
          const filteredResponse = await fetch(filteredUrl.href, { credentials: 'same-origin' });
          if (!filteredResponse.ok) throw new Error(`Filtered collection request failed (${filteredResponse.status})`);
          const filteredHtml = new DOMParser().parseFromString(await filteredResponse.text(), 'text/html');
          const productGrid = filteredHtml.querySelector('#ProductGridContainer #product-grid, #product-grid');
          const items = productGrid?.querySelectorAll(':scope > li.grid__item') || [];
          for (const item of items) {
            if (alternativeCards.children.length >= 6) break;
            const link = item.querySelector('a[href*="/products/"]');
            if (!link) continue;
            const productUrl = new URL(link.href, window.location.origin);
            const productHandle = productHandleFromUrl(productUrl.href);
            if (!productHandle || productHandle === currentHandle || seenProducts.has(productHandle)) continue;
            if (!productHasPurchasableSize(item, size)) continue;
            seenProducts.add(productHandle);
            const card = createAlternativeCard(item, size);
            if (card) alternativeCards.append(card);
          }
        } catch (error) {
          requestFailed = true;
          console.warn('[Diva size finder] Could not load alternatives from a collection.', error);
        }
      }
      if (alternativeLoading) alternativeLoading.hidden = true;
      if (!alternativeCards.children.length && alternativeEmpty) {
        alternativeEmpty.hidden = false;
        alternativeEmpty.textContent = requestFailed
          ? 'We could not load size-matched alternatives just now. Please try again later or return to the collection.'
          : `We could not find another product available in size ${size}. Try another size or return to the collection.`;
      }
    }

    function applySizeFilter(sizes, recommendation) {
      const matchedOptions = getFilterOptions(sizes);
      let debugEnabled = false;
      try { debugEnabled = window.localStorage.getItem('divaSizeFinderDebug') === 'true'; } catch (_) { /* Storage may be disabled. */ }
      if (debugEnabled) {
        console.debug('[Diva size finder] Shopify filter values:', matchedOptions.map(({ dataset }) => ({
          recommendedLabel: dataset.label,
          paramName: dataset.param,
          filterValue: dataset.value,
        })));
      }
      const filterAvailable = sizeFinder?.querySelector('[data-size-filter-available]')?.dataset.sizeFilterAvailable === 'true';
      if (!filterAvailable || matchedOptions.length !== sizes.length) {
        if (status) {
          status.hidden = false;
          status.textContent = 'Your size is saved, but this collection does not currently expose a matching Size filter.';
        }
        return false;
      }

      const params = new URLSearchParams(window.location.search);
      const filterParams = [...new Set(options.map((option) => option.dataset.param))];
      filterParams.forEach((name) => params.delete(name));
      matchedOptions.forEach((option) => params.append(option.dataset.param, option.dataset.value));
      params.delete('page');
      const query = params.toString();
      const target = `${window.location.pathname}${query ? `?${query}` : ''}`;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(recommendation));

      const primary = recommendation.primary;
      const secondary = recommendation.secondary;
      if (status) {
        status.hidden = false;
        status.textContent = `Recommended size ${primary}. Showing products available in size ${primary}${secondary ? ` or ${secondary}` : ''}.`;
      }
      if (clearButton) clearButton.hidden = false;
      if (`${window.location.pathname}${window.location.search}` !== target) {
        window.location.assign(target);
        return true;
      }
      return true;
    }

    function clearSizeFilter() {
      sessionStorage.removeItem(STORAGE_KEY);
      const params = new URLSearchParams(window.location.search);
      [...new Set(options.map((option) => option.dataset.param))].forEach((name) => params.delete(name));
      params.delete('page');
      const query = params.toString();
      const target = `${window.location.pathname}${query ? `?${query}` : ''}`;
      if (`${window.location.pathname}${window.location.search}` !== target) {
        window.location.assign(target);
        return;
      }
      if (status) status.hidden = true;
      if (clearButton) clearButton.hidden = true;
    }

    clearButton?.addEventListener('click', clearSizeFilter);

    function calculateBestFit(measurements) {
      const ranked = SIZE_ORDER.filter((size) => SIZE_CHART[size]).map((size) => {
        const chart = SIZE_CHART[size];
        const differences = Object.keys(FIT_WEIGHTS).reduce((result, key) => {
          result[key] = Math.abs(measurements[key] - chart[key]);
          return result;
        }, {});
        const exactMatch = Object.keys(FIT_WEIGHTS).every((key) => measurements[key] === chart[key]);
        const score = exactMatch ? 0 : Object.keys(FIT_WEIGHTS).reduce((total, key) => {
          const normalized = differences[key] / MEASUREMENT_TOLERANCE_CM[key];
          // A size smaller than a body measurement is riskier than extra ease.
          const directionalPenalty = measurements[key] - chart[key] > MEASUREMENT_TOLERANCE_CM[key] ? 1.35 : 1;
          return total + FIT_WEIGHTS[key] * normalized ** 2 * directionalPenalty;
        }, 0);
        return { size, score, differences, exactMatch };
      }).sort((a, b) => Number(b.exactMatch) - Number(a.exactMatch)
        || a.score - b.score
        || SIZE_ORDER.indexOf(a.size) - SIZE_ORDER.indexOf(b.size));
      let debugEnabled = false;
      try { debugEnabled = window.localStorage.getItem('divaSizeFinderDebug') === 'true'; } catch (_) { /* Storage may be disabled. */ }
      if (debugEnabled) {
        console.debug('[Diva size finder] Customer measurements (cm):', measurements);
        console.debug('[Diva size finder] Size chart and scores:', ranked.map(({ size, score, differences, exactMatch }) => ({
          size,
          chart: SIZE_CHART[size],
          differences,
          exactMatch,
          score,
        })));
        console.debug('[Diva size finder] Winning size:', ranked[0]?.size, 'score:', ranked[0]?.score);
      }
      const primary = ranked[0];
      const adjacent = ranked.find((candidate) =>
        Math.abs(SIZE_ORDER.indexOf(candidate.size) - SIZE_ORDER.indexOf(primary.size)) === 1
      );
      const secondary = adjacent && adjacent.score - primary.score <= SECONDARY_SIZE_SCORE_GAP
        ? adjacent.size
        : null;
      return { primary: primary.size, secondary, scores: ranked };
    }

    function normalizeMeasurements() {
      const result = {};
      const invalid = [];
      ['bust', 'waist', 'hips'].forEach((key) => {
        const input = document.getElementById(`measure-${key}`);
        const raw = input?.value.trim() || '';
        const value = Number(raw);
        const cm = currentUnit === 'inch' ? value * 2.54 : value;
        if (!raw || !Number.isFinite(value) || cm < 30 || cm > 250) {
          invalid.push(key);
          if (input) input.setAttribute('aria-invalid', 'true');
        } else {
          result[key] = cm;
          input.removeAttribute('aria-invalid');
        }
      });
      return { result, invalid };
    }

    function submitSizeFinder() {
      const { result, invalid } = normalizeMeasurements();
      if (invalid.length) {
        if (errorMessage) {
          errorMessage.textContent = 'Enter bust, waist and hip measurements as positive numbers (30–250 cm, or the equivalent in inches).';
          errorMessage.style.display = 'block';
        }
        document.getElementById(`measure-${invalid[0]}`)?.focus();
        return;
      }
      if (errorMessage) errorMessage.style.display = 'none';
      const recommendation = calculateBestFit(result);
      const savedRecommendation = {
        primary: recommendation.primary,
        secondary: recommendation.secondary,
        measurements: result,
      };
      if (measurementMode === 'productSizeFinder') {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(savedRecommendation));
        handleProductSizeRecommendation(savedRecommendation, opener);
        return;
      }
      if (measurementMode !== 'collectionSizeFinder') return;
      const applied = applySizeFilter(
        [recommendation.primary, ...(recommendation.secondary ? [recommendation.secondary] : [])],
        savedRecommendation
      );
      if (applied) closePopup();
      else if (errorMessage) {
        errorMessage.textContent = 'We found your closest size, but this collection has no matching Shopify Size filter. Try another collection or use its Size filter.';
        errorMessage.style.display = 'block';
      }
    }

    function submitPreorderMeasurements() {
      const ids = ['measure-bust', 'measure-waist', 'measure-hips', 'measure-trouser', 'measure-sleeve'];
      const inputs = ids.map((id) => document.getElementById(id));
      const heightInputs = currentUnit === 'cm' ? [heightCm] : [heightFt, heightIn];
      let valid = true;
      [...inputs, ...heightInputs].forEach((input) => {
        if (!input) return;
        input.style.border = '';
        if (!input.value.trim()) {
          input.style.border = '1px solid #000';
          valid = false;
        }
      });
      if (!valid) {
        if (errorMessage) {
          errorMessage.textContent = 'All fields are required to save custom measurements.';
          errorMessage.style.display = 'block';
        }
        return;
      }
      if (errorMessage) errorMessage.style.display = 'none';
      const variantId = document.querySelector('.product-variant-id')?.value;
      if (!variantId) return;
      const value = (id) => document.getElementById(id)?.value.trim() || '';
      const formatValue = (inputValue) => `${inputValue} ${currentUnit}`;
      const formattedHeight = currentUnit === 'cm'
        ? `${value('measure-height-cm')} cm`
        : `${value('measure-height-ft') || 0} ft ${value('measure-height-in') || 0} in`;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: {
            'Customized Sizes': 'Yes',
            Unit: currentUnit,
            Bust: formatValue(value('measure-bust')),
            Waist: formatValue(value('measure-waist')),
            Hips: formatValue(value('measure-hips')),
            'Trouser Length': formatValue(value('measure-trouser')),
            Height: formattedHeight,
            'Sleeve Length': formatValue(value('measure-sleeve')),
          },
        }),
      }).then((response) => {
        if (!response.ok) throw new Error('Cart add failed');
        window.location.href = '/cart';
      }).catch(() => {
        if (errorMessage) {
          errorMessage.textContent = 'We could not add this item. Please try again.';
          errorMessage.style.display = 'block';
        }
      });
    }

    document.getElementById('skip-measurements')?.addEventListener('click', () => {
      const mode = measurementMode;
      closePopup();
      if (mode === 'preorder' && typeof window.preorderAgree === 'function') window.preorderAgree();
    });

    // One dispatcher owns measurement submission. Finder modes never enter the preorder path.
    submitButton?.addEventListener('click', (event) => {
      event.preventDefault();
      switch (measurementMode) {
        case 'productSizeFinder':
        case 'collectionSizeFinder':
          submitSizeFinder();
          break;
        case 'preorder':
          submitPreorderMeasurements();
          break;
      }
    });

    function showSavedRecommendation() {
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'); } catch (_) { saved = null; }
      if (!saved || !sizeFinder) return;
      if (status) {
        status.hidden = false;
        const params = new URLSearchParams(window.location.search);
        const activeValues = options
          .filter((option) => params.getAll(option.dataset.param).includes(option.dataset.value))
          .map((option) => option.dataset.label);
        const visibleSizes = activeValues.length ? activeValues.join(' or ') : [saved.primary, saved.secondary].filter(Boolean).join(' or ');
        status.textContent = `Recommended size ${saved.primary}. Showing products available in size ${visibleSizes}.`;
      }
      if (clearButton) clearButton.hidden = false;
      const params = new URLSearchParams(window.location.search);
      const hasSizeFilter = [...new Set(options.map((option) => option.dataset.param))].some((name) => params.has(name));
      if (!hasSizeFilter) applySizeFilter([saved.primary, ...(saved.secondary ? [saved.secondary] : [])], saved);
    }
    showSavedRecommendation();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
